import { useRef, useState } from 'react';
import Popover from '~/components/common/Popover';
import { CoreOfficeDataType } from '~/types/offices';
import { isCapacityFixed, isFlexibilityFixed } from './utils';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import mixpanelTrack from '~/hooks/useMixPanel';

type TProps = {
  office: CoreOfficeDataType;
  daysPerWeek: number;
  requiredCapacity: number;
  pricePerSeatAfterDiscounts: number;
};

const PricePerSeatPopover = ({ office, requiredCapacity, daysPerWeek, pricePerSeatAfterDiscounts }: TProps) => {
  const { t } = useTranslation();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const triggerRef = useRef(null);

  const capacityFixed = isCapacityFixed(office.minCapacity, office.capacity);
  const flexibilityFixed = isFlexibilityFixed(office.flexibility, office.partTimeDays);

  return (
    <>
      <div className="d-flex">
        <div className="position-relative">
          <span
            className="text-2 -regular text-decoration-underline"
            role="button"
            onClick={() => {
              if (!popoverOpen) mixpanelTrack('E.price_tooltip_click');
              setPopoverOpen(!popoverOpen);
            }}
            ref={triggerRef}
          >
            {getMonthlyPricePerSeatLabel(flexibilityFixed, daysPerWeek, t)}
          </span>
          <Popover
            triggerRef={triggerRef}
            arrowPosition="top-right"
            popoverPosition="bottom-right"
            isOpen={popoverOpen}
            onClose={() => setPopoverOpen(false)}
            width={400}
            title={t('office.show.requestVisitCard.popover.pricePerSeat.title')}
          >
            <div
              className="text-1"
              dangerouslySetInnerHTML={{
                __html: getPopoverBody(
                  capacityFixed,
                  flexibilityFixed,
                  requiredCapacity,
                  daysPerWeek,
                  pricePerSeatAfterDiscounts,
                  t,
                ),
              }}
            />
          </Popover>
        </div>
      </div>
    </>
  );
};

const getMonthlyPricePerSeatLabel = (flexibilityFixed: boolean, daysPerWeek: number, t: TFunction) => {
  if (flexibilityFixed) {
    return t('office.show.requestVisitCard.popover.pricePerSeat.label.flexibilityFixed');
  }
  return t('office.show.requestVisitCard.popover.pricePerSeat.label.flexibilityNotFixed', {
    days: daysPerWeek,
    dayPluralization: t('global.pluralization.i18next.day', { count: daysPerWeek }),
  });
};

export default PricePerSeatPopover;

const getPopoverBody = (
  flexibilityFixed: boolean,
  capacityFixed: boolean,
  requiredCapacity: number,
  daysPerWeek: number,
  pricePerSeatAfterDiscounts: number,
  t: TFunction,
) => {
  if (flexibilityFixed && capacityFixed) {
    return t('office.show.requestVisitCard.popover.pricePerSeat.body.capacityAndFlexibilityFixed', {
      amount: pricePerSeatAfterDiscounts,
    });
  } else if (flexibilityFixed) {
    return t('office.show.requestVisitCard.popover.pricePerSeat.body.flexibilityFixed', {
      capacity: requiredCapacity,
      amount: pricePerSeatAfterDiscounts,
    });
  } else if (capacityFixed) {
    if (daysPerWeek === 5) {
      return t('office.show.requestVisitCard.popover.pricePerSeat.body.capacityFixed.fullTime', {
        days: daysPerWeek,
        amount: pricePerSeatAfterDiscounts,
      });
    } else {
      return t('office.show.requestVisitCard.popover.pricePerSeat.body.capacityFixed.partTime', {
        days: daysPerWeek,
        amount: pricePerSeatAfterDiscounts,
        dayPluralization: t('global.pluralization.i18next.day', { count: daysPerWeek }),
      });
    }
  }
  if (daysPerWeek === 5) {
    return t('office.show.requestVisitCard.popover.pricePerSeat.body.nothingFixed.fullTime', {
      capacity: requiredCapacity,
      amount: pricePerSeatAfterDiscounts,
    });
  } else {
    return t('office.show.requestVisitCard.popover.pricePerSeat.body.nothingFixed.partTime', {
      capacity: requiredCapacity,
      days: daysPerWeek,
      amount: pricePerSeatAfterDiscounts,
      dayPluralization: t('global.pluralization.i18next.day', { count: daysPerWeek }),
    });
  }
};
